<script setup lang="ts">
import type {
  IApartment,
  IPlan,
  ISectionGenPlan,
} from '~/modules/RealEstate/api';
import UiTabsPreload from '~/modules/RealEstate/components/UiTabsPreload.vue';
import UiTab from '@/components/Ui/Tabs/UiTab.vue';
import FloorPlan from '~/modules/RealEstate/components/FloorPlan.vue';
import {useLocale} from '~/plugins/Localization';
import GenplanEntrace from '~/modules/RealEstate/components/GenplanEntrace.vue';
import GenplanModal from '~/modules/RealEstate/components/GenplanModal.vue';
import {useBaseStore} from '~/modules/BaseModule/base.store';
const props = defineProps({
  house: {type: Array as () => ISectionGenPlan[]},
});

const tooltipRef = ref();
const tooltip = ref({
  square: '2',
  title: '',
  price: '',
});
interface IModal {
  show: boolean;
  title: string;
  images: string[];
  flat: IApartment;
  section: number;
  floor: number;
}
const modal = ref<IModal>({
  show: false,
  title: '',
  images: [],
  flat: {} as IApartment,
  section: 1,
  floor: 1,
});
const base = useBaseStore();
const locale = useLocale();
const route = useRoute();
const activeSection = ref(0);
const currentStreet = ref(route.query.street as string);
const finishDownload = ref(false);
function changeSection(index: number) {
  if (index > props.house.length) {
    activeSection.value = props.house.length;
  } else {
    activeSection.value = index;
  }
}
watch(activeSection, () => {
  updatePoly();
});
watch(props.house, (e) => {
  if (props.house) {
    finishDownload.value = true;
    currentStreet.value = route.query.street as string;
  }
});

function updatePoly() {
  console.debug('updatePoly');
}
const filteredHouse = (houses: Array<ISectionGenPlan>) => {
  return houses
    .filter((house) => house.plans.length)
    .filter((house) => {
      return !house.plans
        .map((plan) => {
          return !!plan.floors.length;
        })
        .includes(false);
    });
};

const filteredPlans = (plans: Array<IPlan>) => {
  return plans.sort((a, b) => {
    return Number(a.floors[0].name) - Number(b.floors[0].name);
  });
};
function translate(name: string) {
  const word = name.split(' ');
  const translated = locale.getRealty(word[0]);
  return translated !== `${name}_No_translated`
    ? translated + ' ' + word[1]
    : name;
}
function mousemoveHandler(e: any, {title, price, square}: any) {
  const {clientX, clientY} = e;
  tooltip.value = {title, price, square};
  tooltipRef.value.classList.add('show');
  tooltipRef.value.style.top = clientY + 'px';
  tooltipRef.value.style.left = clientX + 'px';
}
function mouseleaveHandler() {
  tooltipRef.value.classList.remove('show');
}
function openModal(room: IApartment, section: number, floor: number) {
  modal.value.title = translate(room.name);
  modal.value.flat = room;
  modal.value.show = true;
  modal.value.section = section + 1;
  modal.value.floor = floor + 1;
  modal.value.images = [...room.gallery];
}

const isMobile = computed(() => base.isMobile);

const generateSvg = computed(() => {
  const svgElem = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'svg',
  );
  for (const house of filteredHouse(props.house)) {
    const points = (house.points as string)?.split(' ');
    if (points) {
      const svgPath =
        `M${points[0]}` +
        points
          .slice(1)
          .map((point) => ` L${point}`)
          .join('');
      const pathElement = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path',
      );
      pathElement.setAttribute('d', svgPath);
      svgElem.appendChild(pathElement);
    }
  }
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElem);
  return svgString;
});
</script>

<template>
  <div class="genplan-container">
    <div ref="tooltipRef" class="genplan__tooltip">
      <span class="dot" />
      <div class="genplan__tooltip-title">
        {{ tooltip.title }}
      </div>
      <div class="genplan__tooltip-aside">
        <div v-if="tooltip.square" class="genplan__tooltip-square">
          {{ tooltip.square }}
        </div>
      </div>
    </div>
    <div class="genplan__top">
      <!-- <ui-selection-tabs
        class="genplan__selections"
        :title="$t('count')"
        :items="rooms"
        @change="filterRooms"
      /> -->
      <!-- <button
        class="d-button secondary borderless bright-accent"
        @click="goToList"
      >
        {{ $t('viewList') }}
      </button> -->
    </div>
    <!--    -->
    <div
      v-if="!house.length"
      class="genplan__main genplan__main_preloader"
    >
      <UiTabsPreload>
        <div class="preload center" />
      </UiTabsPreload>

      <div class="preload right" />
    </div>
    <div v-else class="genplan__main">
      <template v-for="(section, index) in filteredHouse(house)">
        <ui-tabs-v2
          v-if="activeSection === index && section.plans.length"
          :key="index"
          :direction="isMobile ? 'horizontal' : 'vertical'"
          @changed="updatePoly"
        >
          <template
            v-for="(plan, indexPlan) in filteredPlans(section.plans)"
          >
            <ui-tab
              v-if="plan"
              :key="indexPlan"
              :name="plan.floors[0].name"
              :uuid="plan.floors[0].uuid"
            >
              <FloorPlan
                v-if="plan && plan.floors.length"
                :plan="plan"
                @mousemoveApartment="mousemoveHandler"
                @mouseleaveApartment="mouseleaveHandler"
                @openModalApartment="
                  openModal($event, index, indexPlan)
                "
              />
            </ui-tab>
          </template>
        </ui-tabs-v2>
      </template>
      <div v-if="generateSvg" class="genplan__main-entrance">
        <div v-if="currentStreet" class="genplan__main-street">
          {{ $realty(currentStreet) }}
        </div>
        <GenplanEntrace
          :contour="generateSvg"
          :img="house[0].preview"
          @change="changeSection($event)"
        />
      </div>
    </div>
    <GenplanModal
      v-model="modal.show"
      :title="modal.title"
      :images="modal.images"
      :flat="modal.flat"
      :section="modal.section"
      :floor="modal.floor"
    />
  </div>
</template>

<style scoped lang="scss">
.genplan__main {
  &-entrance {
    max-height: fit-content;
    overflow: hidden;
  }
  .preload {
    border-radius: 16px;
    &.left {
      background: linear-gradient(
        100deg,
        #1d2c3b 36.98%,
        #273b4f 49.48%,
        #1d2c3b 61.46%
      );
      animation: 1.5s shine linear infinite;
      background-size: 200% 100%;
      height: 468px;
      width: 40px;
      margin-bottom: 16px;
    }
    &.center {
      margin-top: 10px;
      background: linear-gradient(
        107deg,
        #f6f8fd 36.98%,
        #e1e5f0 49.48%,
        #f6f8fd 61.46%
      );
      animation: 3s shine linear infinite;
      background-size: 200% 100%;
      height: 500px;
      width: 500px;
      margin-bottom: 16px;
    }
    &.right {
      background: linear-gradient(
        107deg,
        #f6f8fd 36.98%,
        #e1e5f0 49.48%,
        #f6f8fd 61.46%
      );
      animation: 3s shine linear infinite;
      background-size: 200% 100%;
      height: 240px;
      width: 200px;
    }
  }

  @media (max-width: $lg) {
    flex-direction: column-reverse;
    align-items: center;
  }
}
@keyframes shine {
  to {
    background-position-x: -200%;
  }
}
.genplan {
  position: relative;
  &-img {
    width: 100%;
    max-height: 100vh;
  }

  &__tooltip {
    position: fixed;
    overflow: visible;
    z-index: 1000;
    background-color: #2491e7;
    border-radius: 8px;
    padding: 6px 8px;
    width: max-content;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    transform: translate(-50%, 30px);

    &.show {
      opacity: 1;
    }

    &-aside {
      display: flex;
      gap: 32px;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.02em;
      color: #ffffff;
    }

    &-title {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.02em;
      color: #ffffff;
      margin-bottom: 8px;
    }

    .dot {
      display: block;
      width: 0;
      height: 0;
      position: absolute;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 8px solid var(--accent);
      top: -8px;
      bottom: 100%;
      left: 50%;
      transform: translate(-50%, 0) rotate(0deg);
    }
  }

  &__main {
    display: flex;
    justify-content: space-between;

    &-street {
      font-weight: 400;
      font-size: 16px;
      line-height: 140%;
      border-top: 1.5px solid #7f97aa;
      border-bottom: 1.5px solid #7f97aa;
      padding: 0 15px;
      text-align: center;
      letter-spacing: 0.03em;
      color: #64686e;
      margin-bottom: 16px;
      width: fit-content;
    }

    &-rooms {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      &-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.02em;
        color: #ffffff;

        &:before {
          content: '';
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        &--entry {
          &:before {
            background: #839aaf;
          }
        }

        &--st {
          &:before {
            background: #e7e7e7;
          }
        }

        &--1k:before {
          background: #f9e161;
        }

        &--2k:before {
          background: #caa6e6;
        }

        &--3k:before {
          background: #8fcfe9;
        }

        &--4k:before {
          background: #f8d4be;
        }
      }
    }
  }

  &-container {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    margin: auto;
    max-width: 1128px;
    width: 100%;

    ::v-deep(.ui-tabs__panel) {
      margin: 0 auto;
    }

    ::v-deep(.ui-tabs) {
      flex: 1;
    }
  }

  &__top {
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: $lg) {
      margin-bottom: 0px;
    }
  }
}
.genplan__main_preloader {
  @media (max-width: $lg) {
    flex-direction: column;
    .ui-tabs {
      display: none;
    }
    .preload {
      width: 100% !important;
    }
  }
}
</style>
